import { useState, useEffect, Dispatch, SetStateAction, useRef } from 'react';
import { useVehicleRepository } from './useVehicleRepository';
import { Vehicle } from 'core/models/vehicle';
import { Project } from 'core/models/project';
import { timestampFromOclock } from 'utility/timestampFromOclock';
import { calcDelay } from 'services/utility/calcDelay';

export function useStandBy(vehicleId: Vehicle.Id, projectId: Project.Id, freq: number): [boolean, Dispatch<SetStateAction<boolean>>] {
    if (freq <= 0) {
        throw new Error(`heartbeat frequency must be >= 0, but ${freq} given`);
    }

    const [standby, setStandBy] = useState(false);
    const vehicleRepository = useVehicleRepository();

    useEffect(() => {
        if (!standby) return;

        const interval = window.setInterval(() => {
            vehicleRepository.postHeartbeat(vehicleId, projectId);
        }, 1000 / freq);

        return () => window.clearInterval(interval);
    }, [freq, projectId, standby, vehicleId, vehicleRepository]);

    return [standby, setStandBy];
}

export type HeartbeatResult
    = { status: 'beating' }
    | { status: 'stopped' }
    | { status: 'rejected' }
    | { status: 'too-slow', delay: number, timeout: number, receivedAt: number, sentAt: number }
    ;

export function useHeartbeatHealth(vehicleId: Vehicle.Id, projectId: Project.Id, standby: boolean, freq: number, timeout: number) {
    if (freq <= 0) {
        throw new Error(`heartbeat frequency must be >= 0, but ${freq} given`);
    }

    const vehicleRepository = useVehicleRepository();

    const lastResponseRef = useRef<{ timestamp: number } | 'rejected'>();
    useEffect(() => {
        if (!standby) return;

        const listener = (timestamp: number, accept: boolean) => {
            if (accept) {
                lastResponseRef.current = { timestamp };
            } else {
                lastResponseRef.current = 'rejected';
            }
        };
        vehicleRepository.addVehicleHeartbeatListener(vehicleId, projectId, listener);
        return () => vehicleRepository.removeVehicleHeartbeatListener(vehicleId, projectId, listener);
    }, [projectId, standby, vehicleId, vehicleRepository]);

    const [isHealthy, setHealthy] = useState<HeartbeatResult>({ status: 'stopped' });
    useEffect(() => {
        if (!standby) {
            setHealthy({ status: 'stopped' });
            return;
        }

        const interval = window.setInterval(() => {
            const lastResponse = lastResponseRef.current;
            if (lastResponse === undefined) {
                setHealthy({ status: 'stopped' });
            } else if (lastResponse === 'rejected') {
                setHealthy({ status: 'rejected' });
            } else {
                const sentAt = lastResponse.timestamp;
                const receivedAt = timestampFromOclock();
                const delay = calcDelay(sentAt, receivedAt); // upstream delay + downstream delay
                if (0 <= delay && delay <= timeout) {
                    setHealthy({ status: 'beating' });
                } else {
                    setHealthy({ status: 'too-slow', delay, timeout, receivedAt, sentAt });
                }
            }
        }, 1000 / freq);
        return () => window.clearInterval(interval);
    }, [freq, standby, timeout]);

    return isHealthy;
}
